import React, {
  ReactNode,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { ArrowRightCarousel } from "components/atoms/Icon";
import classNames from "classnames";
import "./index.scss";
import { useScreenSize } from "hooks";
import { MOBILE_WIDTH } from "utils/constants";

const MIN_ITEM_WIDTH = 340;
const DRILL_ITEM_WIDTH = 290;
const PROGRESS_ITEM_MIN_WIDTH = 200;
const AI_RECOMMENDATION_ITEM_MIN_WIDTH = 190;

interface IProps {
  children: ReactNode | ReactNode[];
  isShowSIdeNavigation?: boolean;
  theme?: "light" | "dark";
  renderStyle?: "default" | "drill" | "progress" | "aiRecommendation";
  forceOneItemPerSlide?: boolean;
}

export const Carousel: React.FC<IProps> = ({
  children,
  isShowSIdeNavigation,
  theme,
  renderStyle = "default",
  forceOneItemPerSlide = false,
}) => {
  const screen = useScreenSize();
  const [activeIndex, setActiveIndex] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(3);
  const [totalSlides, setTotalSlides] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDotClick = (index: number) => {
    setActiveIndex(index);
  };

  const handleNextClick = () => {
    setActiveIndex((prev) => (prev < totalSlides - 1 ? prev + 1 : prev));
  };

  const handlePrevClick = () => {
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const calculateItemsPerSlide = useCallback(() => {
    if (!containerRef.current) return;

    if (forceOneItemPerSlide) {
      setItemsPerSlide(1);
      return;
    }

    const containerWidth = containerRef.current.clientWidth;
    const drillCardElement = document.querySelector(".drill-card-wrapper");

    const isProgressStyle = renderStyle === "progress";
    const isDrillStyle = renderStyle === "drill";
    const isAiRecommendationStyle = renderStyle === "aiRecommendation";

    const itemWidth = isProgressStyle
      ? PROGRESS_ITEM_MIN_WIDTH
      : isDrillStyle
      ? drillCardElement
        ? drillCardElement.clientWidth
        : DRILL_ITEM_WIDTH
      : isAiRecommendationStyle
      ? AI_RECOMMENDATION_ITEM_MIN_WIDTH
      : MIN_ITEM_WIDTH;

    const maxItemsPerSlideAllowed = isProgressStyle
      ? 5
      : isDrillStyle || isAiRecommendationStyle
      ? 4
      : 3;

    let calculatedItems = Math.floor(containerWidth / itemWidth);

    if (
      (isProgressStyle || isAiRecommendationStyle) &&
      screen.width < MOBILE_WIDTH
    ) {
      calculatedItems = 1;
    }

    const newItemsPerSlide = Math.max(
      1,
      Math.min(maxItemsPerSlideAllowed, calculatedItems)
    );

    if (newItemsPerSlide !== itemsPerSlide) {
      setItemsPerSlide(newItemsPerSlide);
      setActiveIndex(0);
    }
  }, [itemsPerSlide, renderStyle, screen.width, forceOneItemPerSlide]);

  useEffect(() => {
    if (!containerRef.current) return;
    calculateItemsPerSlide();
    const resizeObserver = new ResizeObserver(() => {
      calculateItemsPerSlide();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateItemsPerSlide]);

  // const filteredChildren = children.filter((child) => child !== false);
  const childrenArray = Array.isArray(children) ? children : [children];
  const filteredChildren = childrenArray.filter((child) => child !== false);

  useEffect(() => {
    const totalItems = filteredChildren.length;
    const slides = Math.ceil(totalItems / Math.max(1, itemsPerSlide));
    setTotalSlides(slides);
  }, [filteredChildren.length, itemsPerSlide]);

  return (
    <div className="carousel-parent">
      {isShowSIdeNavigation && activeIndex > 0 ? (
        <div className="carousel-left-nav" onClick={handlePrevClick}>
          <ArrowRightCarousel
            fillCOlor={theme === "light" ? "#fff" : "#000"}
            className="rotate-left"
          />
        </div>
      ) : null}
      {isShowSIdeNavigation && activeIndex < totalSlides - 1 ? (
        <div className="carousel-right-nav" onClick={handleNextClick}>
          <ArrowRightCarousel fillCOlor={theme === "light" ? "#fff" : "#000"} />
        </div>
      ) : null}
      <div className="carousel-container" ref={containerRef}>
        <div
          className="carousel-track"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {Array.from({ length: totalSlides }).map((_, slideIndex) => {
            const startIndex = slideIndex * itemsPerSlide;
            const slideItems = filteredChildren.slice(
              startIndex,
              startIndex + itemsPerSlide
            );
            return (
              <div
                key={slideIndex}
                className={classNames({
                  "carousel-slide": true,
                  "multiple-carousel-slides": totalSlides > 1,
                  [`active-${renderStyle}-carousel`]:
                    slideIndex === activeIndex,
                  [`items-per-slide-${itemsPerSlide}`]: true,
                })}
              >
                {renderStyle === "progress"
                  ? slideItems.map((child, itemIndex) => (
                      <React.Fragment
                        key={`progress-item-${startIndex + itemIndex}`}
                      >
                        <div className="carousel-item">{child}</div>
                        {itemIndex < slideItems.length - 1 && (
                          <div className="doughnut-chart-small-card-divider"></div>
                        )}
                      </React.Fragment>
                    ))
                  : slideItems.map((child, itemIndex) => (
                      <div
                        key={startIndex + itemIndex}
                        className="carousel-item"
                      >
                        {child}
                      </div>
                    ))}
              </div>
            );
          })}
        </div>
      </div>
      <div className="carousel-dots">
        {Array.from({ length: totalSlides }).map((_, index) => (
          <div
            key={index}
            className={`dot ${index === activeIndex ? "active" : ""}`}
            onClick={() => handleDotClick(index)}
            id={`dot-${index}`}
          />
        ))}
      </div>
    </div>
  );
};
